import { onBeforeUnmount, watch, type ComputedRef } from 'vue';

interface SpySection {
  id: string;
  el: HTMLElement;
}

/**
 * Activation line as a fraction of the scroll root height from its top.
 * 0.18 ≈ upper fifth — a section becomes active as soon as its header reaches
 * that line (not only when it fills the old 35% "band").
 */
const ACTIVATION_LINE_RATIO = 0.18;

function isScrollableOverflowY(oy: string): boolean {
  return oy === 'auto' || oy === 'scroll' || oy === 'overlay';
}

/**
 * Every overflow-y ancestor that *could* be a scrollport (tab panels, screen
 * main, sticky columns, …). We attach listeners to all of them because the
 * first candidate is not always the one that actually scrolls (content may
 * still be loading when the spy first mounts).
 */
export function collectSectionScrollRoots(el: HTMLElement | null | undefined): HTMLElement[] {
  const roots: HTMLElement[] = [];
  let p = el?.parentElement ?? null;
  while (p) {
    if (isScrollableOverflowY(getComputedStyle(p).overflowY)) {
      roots.push(p);
    }
    p = p.parentElement;
  }
  return roots;
}

/**
 * Nearest overflow-y scrollport. Prefers one that currently has overflow room;
 * otherwise returns the nearest overflow:auto/scroll ancestor so we still attach
 * before content finishes laying out (tables, async sections).
 */
export function findSectionScrollRoot(el: HTMLElement | null | undefined): HTMLElement | null {
  const roots = collectSectionScrollRoots(el);
  for (const r of roots) {
    if (r.scrollHeight - r.clientHeight > 4) return r;
  }
  return roots[0] ?? null;
}

function pickPrimaryRoot(roots: HTMLElement[]): HTMLElement | null {
  // Prefer the deepest still-scrollable ancestor — usually `.layout-tab-panels`.
  // Walk from nearest to farthest so nested panels win over window-ish shells.
  for (const r of roots) {
    if (r.scrollHeight - r.clientHeight > 4) return r;
  }
  return roots[0] ?? null;
}

/**
 * Scroll-spy for the observer-free SectionNav registry.
 * Geometry-first: the active section is the last one whose top edge is still
 * above the activation line (upper ~18% of the scroll root). That lights the
 * scrubber as soon as you *enter* a section, not when you have almost scrolled
 * past it. `pause()` suppresses updates during programmatic jumps.
 */
export function useSectionScrollSpy(opts: {
  items: ComputedRef<SpySection[]>;
  setActive: (id: string | null) => void;
  suppressed?: () => boolean;
  /**
   * The registry's live active id. When it changes to a value the spy did NOT
   * set itself (a scrubber click, a programmatic jump), the spy freezes until
   * the resulting smooth scroll settles — otherwise every intermediate section
   * passing the activation line would flicker through the rail/functions panel
   * and the last recompute could even "win" over the clicked target.
   */
  activeId?: ComputedRef<string | null>;
}): { pause: (ms?: number) => void } {
  let scrollRoots: HTMLElement[] = [];
  let pausedUntil = 0;
  let raf = 0;
  let resizeObserver: ResizeObserver | null = null;
  let lastActiveId: string | null = null;

  /** External-jump freeze: active until scroll events stop for SETTLE_MS. */
  let jumping = false;
  let settleTimer = 0;
  let jumpFailsafe = 0;
  const SETTLE_MS = 250;
  const JUMP_MAX_MS = 3000;

  function endJump(): void {
    jumping = false;
    if (settleTimer) window.clearTimeout(settleTimer);
    if (jumpFailsafe) window.clearTimeout(jumpFailsafe);
    settleTimer = 0;
    jumpFailsafe = 0;
  }

  function beginJump(): void {
    if (jumpFailsafe) window.clearTimeout(jumpFailsafe);
    jumping = true;
    resetSettle();
    // A jump must never freeze the spy forever (e.g. async content keeps
    // firing resize-driven pseudo-scrolls) — hard stop after JUMP_MAX_MS.
    jumpFailsafe = window.setTimeout(endJump, JUMP_MAX_MS);
  }

  function resetSettle(): void {
    if (settleTimer) window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(endJump, SETTLE_MS);
  }

  /**
   * Tail sweep: sections near the end of a document can be too short for their
   * top to ever reach the fixed line — a hard "at bottom → last section"
   * override then SKIPS every short section between the last tall one and the
   * end. Instead, over the final viewport-worth of scroll the activation line
   * moves proportionally from its 18% resting position down to the bottom
   * edge, so each trailing section still activates in document order (and at
   * maximum scroll the last one wins, as before).
   */
  function activationLineOffset(height: number, remainingScroll: number): number {
    const base = height * ACTIVATION_LINE_RATIO;
    const tailZone = height * (1 - ACTIVATION_LINE_RATIO);
    const shift = Math.max(0, tailZone - Math.max(0, remainingScroll));
    return Math.min(height, base + shift);
  }

  function activationLineY(): number {
    const root = pickPrimaryRoot(scrollRoots);
    if (root) {
      const r = root.getBoundingClientRect();
      // Guard zero/hidden roots (jsdom, display:none mid-layout) — fall back
      // to the viewport so we don't pin activation at y=0 forever.
      if (r.height > 1) {
        const remaining = root.scrollHeight - root.clientHeight - root.scrollTop;
        return r.top + activationLineOffset(r.height, remaining);
      }
    }
    const doc = document.documentElement;
    const remaining = doc.scrollHeight - window.innerHeight - window.scrollY;
    return activationLineOffset(window.innerHeight, remaining);
  }

  function recompute(): void {
    if (jumping || Date.now() < pausedUntil || opts.suppressed?.()) return;
    const sections = opts.items.value;
    if (sections.length === 0) return;

    // Re-pick roots when content has grown (async tables) without a re-register.
    if (
      scrollRoots.length === 0 ||
      scrollRoots.every((r) => r.scrollHeight - r.clientHeight <= 4)
    ) {
      const next = collectSectionScrollRoots(sections[0].el);
      if (next.length) {
        for (const root of scrollRoots) {
          root.removeEventListener('scroll', onScroll);
        }
        scrollRoots = next;
        for (const root of scrollRoots) {
          root.addEventListener('scroll', onScroll, { passive: true });
        }
      }
    }

    const line = activationLineY();
    // Document order: last section whose top has crossed the activation line
    // (which sweeps toward the bottom edge over the final stretch of scroll —
    // see activationLineOffset).
    let active: SpySection = sections[0];
    for (const s of sections) {
      if (s.el.getBoundingClientRect().top <= line) {
        active = s;
      } else {
        break;
      }
    }
    if (active.id !== lastActiveId) {
      lastActiveId = active.id;
      opts.setActive(active.id);
    }
  }

  /**
   * Whether a scroll event can have moved the sections: the page, a scrollport
   * around them, the window. A scroll inside something that holds none of them
   * — an open editor, a popover, a table — moves no section, and the geometry
   * would read the unmoved page as the top and fall back to the first section
   * (BLP/nx#2794). Calls without an event (resize, ResizeObserver) always count.
   */
  function movesSections(event?: Event): boolean {
    const target = event?.target;
    if (event?.type !== 'scroll' || !(target instanceof Element)) return true;
    const sections = opts.items.value;
    // Any section: a panel scrolling the later sections moves them although
    // the first one sits outside it.
    return sections.length === 0 || sections.some((s) => target.contains(s.el));
  }

  function onScroll(event?: Event): void {
    if (!movesSections(event)) return;
    if (jumping) {
      // Programmatic smooth scroll in flight: keep the freeze alive until the
      // stream of scroll events dries up, then resume normal spying.
      resetSettle();
      return;
    }
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      recompute();
    });
  }

  function detachScrollRoots(): void {
    for (const root of scrollRoots) {
      root.removeEventListener('scroll', onScroll);
    }
    scrollRoots = [];
    if (typeof document !== 'undefined') {
      document.removeEventListener('scroll', onScroll, true);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }
    resizeObserver?.disconnect();
    resizeObserver = null;
  }

  function rebuild(sections: SpySection[]): void {
    detachScrollRoots();
    // Keep an already-active id (external click, adopted jump) across a
    // re-register — item signatures also change when async content connects,
    // and blindly resetting here let the follow-up recompute overroll a
    // just-clicked section with the geometric pick.
    lastActiveId = opts.activeId?.value ?? null;
    if (sections.length === 0) return;

    // Listen on every overflow ancestor — content height often arrives after
    // the first registration (async tables), so the "currently scrollable"
    // pick alone would leave us stuck on `window` while the user scrolls a
    // nested `.layout-tab-panels`.
    scrollRoots = collectSectionScrollRoots(sections[0].el);
    for (const root of scrollRoots) {
      root.addEventListener('scroll', onScroll, { passive: true });
    }
    if (typeof document !== 'undefined') {
      // Capture phase: scroll does not bubble, but *does* fire on the
      // scrolling element during capture from document → target. Catches any
      // scrollport we missed (or that became scrollable later).
      document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }

    // When tables / images grow, recompute active section.
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => onScroll());
      for (const s of sections) resizeObserver.observe(s.el);
      for (const root of scrollRoots) resizeObserver.observe(root);
    }

    // Initial pick only while nothing is active yet: with a live active id the
    // spy stays quiet until a real scroll/resize event — a rebuild must never
    // overrule an activation that just happened.
    if (lastActiveId == null) {
      recompute();
    }
  }

  // Track id+el identity only — do NOT rebuild on activeId flips (that would
  // re-bind listeners every scroll tick via the registry's `items` projection).
  const stop = watch(
    () => opts.items.value.map((s) => `${s.id}:${s.el.isConnected ? '1' : '0'}`).join('|'),
    () => rebuild(opts.items.value),
    { immediate: true }
  );

  // Detect EXTERNAL activation (rail click, section click): the spy records
  // every id it sets itself in `lastActiveId`, so any other value must come
  // from outside. Adopt it and freeze until the ensuing smooth scroll settles;
  // without a follow-up scroll the freeze ends after SETTLE_MS and the choice
  // simply stands until the user scrolls again (recompute is event-driven).
  const stopActiveWatch = opts.activeId
    ? watch(opts.activeId, (id) => {
        if (id === lastActiveId) return;
        lastActiveId = id;
        beginJump();
      })
    : undefined;

  onBeforeUnmount(() => {
    stop();
    stopActiveWatch?.();
    endJump();
    if (raf) cancelAnimationFrame(raf);
    detachScrollRoots();
  });

  return {
    pause(ms = 800) {
      pausedUntil = Date.now() + ms;
    },
  };
}
